import React, { useEffect, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { api } from "../lib/api";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { ArrowLeft, Pencil, History, User, Building2, Phone, Mail, DollarSign, Sparkles, Clock, Paperclip, GitBranch, CheckCircle2, FileText, MessageSquare, Target, FileSignature } from "lucide-react";
import { toast } from "sonner";
import LeadAnexos from "../components/LeadAnexos";
import FunilTimeline from "../components/FunilTimeline";
import WizardFechamento from "../components/WizardFechamento";
import PedidoVendaForm from "../components/PedidoVendaForm";
import IAEstrategicaDialog from "../components/IAEstrategicaDialog";
import { etapaInfo } from "../lib/etapasFunil";
import PedidosLead from "../components/PedidosLead";
import ConversasLead from "../components/ConversasLead";
import PropostaNativaForm from "../components/PropostaNativaForm";
import PropostasNativasLead from "../components/PropostasNativasLead";
import AssinaturasLead from "../components/assinaturas/AssinaturasLead";

const fmtMoney = (v) =>
  new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(v || 0);

const fmtData = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("pt-BR", { dateStyle: "short", timeStyle: "short" });
  } catch {
    return iso;
  }
};

const LABEL_CAMPO = {
  nome: "Nome",
  empresa: "Empresa",
  cnpj: "CNPJ",
  telefone: "Telefone",
  email: "E-mail",
  origem: "Origem",
  valor_estimado: "Valor estimado",
  vendedor_id: "Vendedor responsável",
  observacoes: "Observações",
  status_funil: "Etapa do funil",
};

export default function LeadDetalhe() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [lead, setLead] = useState(null);
  const [historico, setHistorico] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState(searchParams.get("tab") || "dados");

  useEffect(() => {
    const carregar = async () => {
      setLoading(true);
      try {
        const [{ data: l }, { data: h }] = await Promise.all([
          api.get(`/leads/${id}`),
          api.get(`/leads/${id}/historico`),
        ]);
        setLead(l);
        setHistorico(h.items || []);
      } catch (e) {
        toast.error(e?.response?.data?.detail || "Não foi possível carregar o lead");
        navigate("/app/leads");
      } finally {
        setLoading(false);
      }
    };
    carregar();
  }, [id, navigate]);

  if (loading) {
    return <div className="p-6 text-muted-foreground">Carregando…</div>;
  }
  if (!lead) return null;

  return (
    <div className="space-y-5" data-testid="lead-detalhe-page">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={() => navigate("/app/leads")}>
            <ArrowLeft size={16} className="mr-1" /> Voltar
          </Button>
          <div>
            <h1 className="text-2xl font-display font-bold" style={{ color: "#1B3A6B" }}>
              {lead.nome}
            </h1>
            <p className="text-sm text-muted-foreground">{lead.empresa}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <IAEstrategicaDialog leadId={lead.id} leadNome={lead.nome} />
          <span
            className="inline-flex items-center gap-1.5 rounded-md border px-3 h-8 text-xs font-semibold"
            style={{ color: etapaInfo(lead.status_funil).color, borderColor: `${etapaInfo(lead.status_funil).color}55`, background: `${etapaInfo(lead.status_funil).color}14` }}
            title="Etapa atual no funil de vendas"
            data-testid="lead-etapa-badge"
          >
            <Target size={13} /> Etapa: {etapaInfo(lead.status_funil).label}
          </span>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="dados"><User size={14} className="mr-1" /> Dados</TabsTrigger>
          <TabsTrigger value="timeline" data-testid="tab-timeline">
            <GitBranch size={14} className="mr-1" /> Linha do Tempo
          </TabsTrigger>
          <TabsTrigger value="pedidos" data-testid="tab-pedidos">
            <FileText size={14} className="mr-1" /> Pedidos
          </TabsTrigger>
          <TabsTrigger value="propostas" data-testid="tab-propostas">
            <FileText size={14} className="mr-1" /> Propostas
          </TabsTrigger>
          <TabsTrigger value="conversas" data-testid="tab-conversas">
            <MessageSquare size={14} className="mr-1" /> Conversas
          </TabsTrigger>
          <TabsTrigger value="anexos" data-testid="tab-anexos">
            <Paperclip size={14} className="mr-1" /> Anexos
          </TabsTrigger>
          <TabsTrigger value="assinaturas" data-testid="tab-assinaturas">
            <FileSignature size={14} className="mr-1" /> Assinaturas
          </TabsTrigger>
          <TabsTrigger value="historico" data-testid="tab-historico">
            <History size={14} className="mr-1" /> Histórico · {historico.length}
          </TabsTrigger>
        </TabsList>

        <TabsContent value="dados">
          <Card className="p-6 space-y-3">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Campo icon={<User size={14} />} label="Contato" value={lead.nome} />
              <Campo icon={<Building2 size={14} />} label="Empresa" value={lead.empresa} />
              <Campo icon={<Building2 size={14} />} label="CNPJ" value={lead.cnpj || "—"} />
              <Campo icon={<Phone size={14} />} label="Telefone" value={lead.telefone || "—"} />
              <Campo icon={<Mail size={14} />} label="E-mail" value={lead.email || "—"} />
              <Campo icon={<DollarSign size={14} />} label="Valor estimado" value={fmtMoney(lead.valor_estimado)} />
              <Campo icon={<Sparkles size={14} />} label="Score IA" value={lead.score_ia ?? "—"} />
              <Campo icon={<Clock size={14} />} label="Última movimentação" value={fmtData(lead.movido_em)} />
            </div>
            {lead.observacoes && (
              <div className="mt-3 p-3 rounded bg-muted">
                <div className="text-xs uppercase text-muted-foreground mb-1">Observações</div>
                <div className="text-sm">{lead.observacoes}</div>
              </div>
            )}
            {lead.score_justificativa && (
              <div className="mt-3 p-3 rounded border-l-2" style={{ borderColor: "#F97316" }}>
                <div className="text-xs uppercase text-muted-foreground mb-1">Análise IA</div>
                <div className="text-sm">{lead.score_justificativa}</div>
                {lead.score_proximo_passo && (
                  <div className="text-sm mt-2"><strong>Próximo passo:</strong> {lead.score_proximo_passo}</div>
                )}
              </div>
            )}
          </Card>
        </TabsContent>

        <TabsContent value="timeline">
          <FunilTimeline leadId={lead.id} onNavigate={(tab) => setActiveTab(tab)} />
          {lead.status_funil !== "efetivado" && (
            <div className="mt-4 flex justify-end gap-2 flex-wrap">
              <PropostaNativaForm lead={lead} onCreated={() => window.location.reload()} />
              <PedidoVendaForm lead={lead} onCreated={() => window.location.reload()} />
              <WizardFechamento lead={lead} onComplete={() => window.location.reload()} />
            </div>
          )}
          {lead.status_funil === "efetivado" && (
            <div className="mt-4 p-3 rounded bg-green-50 text-green-800 text-sm flex items-center gap-2" data-testid="lead-fechado-banner">
              <CheckCircle2 size={16} /> Lead fechado e ativado. Veja o contrato em <strong>/app/minhas-comissoes</strong>.
            </div>
          )}
        </TabsContent>

        <TabsContent value="pedidos">
          <PedidosLead leadId={lead.id} lead={lead} onChange={() => window.location.reload()} />
        </TabsContent>

        <TabsContent value="propostas">
          <PropostasNativasLead leadId={lead.id} lead={lead} onChange={() => window.location.reload()} />
        </TabsContent>

        <TabsContent value="conversas">
          <ConversasLead leadId={lead.id} />
        </TabsContent>

        <TabsContent value="anexos">
          <LeadAnexos leadId={lead.id} leadUnidadeId={lead.unidade_id} />
        </TabsContent>

        <TabsContent value="assinaturas">
          <AssinaturasLead leadId={lead.id} />
        </TabsContent>

        <TabsContent value="historico">
          <Card className="p-6" data-testid="historico-list">
            {historico.length === 0 ? (
              <div className="text-center text-sm text-muted-foreground py-8">
                Nenhuma alteração registrada ainda.
              </div>
            ) : (
              <div className="space-y-4">
                {historico.map((h) => (
                  <div key={h.id} className="border-l-2 pl-4 py-2" style={{ borderColor: h.tipo === "movimentacao" ? "#F97316" : "#1B3A6B" }} data-testid={`hist-${h.id}`}>
                    <div className="flex items-center justify-between mb-2">
                      <div className="text-sm">
                        <strong>{h.usuario_nome}</strong>
                        <span className="text-muted-foreground ml-2">
                          {h.tipo === "movimentacao" ? "moveu o lead" : "editou o lead"}
                        </span>
                      </div>
                      <div className="text-xs text-muted-foreground">{fmtData(h.alterado_em)}</div>
                    </div>
                    <ul className="text-sm space-y-1">
                      {(h.campos || []).map((c, idx) => (
                        <li key={idx} className="text-xs">
                          <span className="font-medium">{LABEL_CAMPO[c.campo] || c.campo}:</span>{" "}
                          <span className="text-muted-foreground line-through">{c.valor_antes || "(vazio)"}</span>
                          {" → "}
                          <span className="font-medium" style={{ color: "#16a34a" }}>{c.valor_depois || "(vazio)"}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            )}
          </Card>
        </TabsContent>
      </Tabs>

      <div>
        <Button variant="outline" onClick={() => navigate("/app/leads")}>
          <Pencil size={14} className="mr-2" /> Voltar para edição
        </Button>
      </div>
    </div>
  );
}

const Campo = ({ icon, label, value }) => (
  <div>
    <div className="flex items-center gap-1 text-xs uppercase text-muted-foreground mb-1">
      {icon} {label}
    </div>
    <div className="text-sm font-medium">{value}</div>
  </div>
);
